import { gsm7, smsLength, smsSegments } from './smsText';
import { interpolate, SampleValue } from './copyModel';

import { CopyLocale } from '@/services/lifecycleAPI';

export type LintIssue = { field: string; message: string; blocksSave: boolean };

type Input = {
  text: Record<string, string>;
  allowed: Record<string, string[]>;
  sampleValues: Record<string, SampleValue>;
  stopLine: string;
  locale?: CopyLocale;
};

const SMS_PREFIX = 'GravyWork: ';
const SMS_LIMIT = 160;
const SPANISH_MAX_SEGMENTS = 2;
const CHANNEL_NEEDS: Record<string, string[]> = {
  SMS: ['sms'],
  push: ['push_body'],
  email: ['subject_a', 'preview', 'headline', 'body', 'button'],
};

const placeholders = (text: string): string[] => {
  const found: string[] = [];
  const pattern = /%\{(\w+)\}/g;
  let match = pattern.exec(text);
  while (match) {
    if (!found.includes(match[1])) found.push(match[1]);
    match = pattern.exec(text);
  }
  return found;
};

export const withoutStopLine = (text: string, stopLine: string): string =>
  stopLine && text.endsWith(` ${stopLine}`)
    ? text.slice(0, -(stopLine.length + 1))
    : text;

const completenessIssues = (text: Record<string, string>): LintIssue[] =>
  Object.entries(CHANNEL_NEEDS).flatMap(([channel, wanted]) => {
    const needed = wanted.filter((field) => field in text);
    const present = needed.filter((field) => text[field].trim() !== '');
    if (present.length === 0 || present.length === needed.length) return [];
    const missing = needed.filter((field) => !present.includes(field));
    return [
      {
        field: missing[0],
        message: `Spanish ${channel} needs every field: ${missing.join(', ')} missing`,
        blocksSave: false,
      },
    ];
  });

export const lintVariant = ({
  text,
  allowed,
  sampleValues,
  stopLine,
  locale = 'en',
}: Input): LintIssue[] => {
  const spanish = locale === 'es';
  const issues: LintIssue[] = spanish ? completenessIssues(text) : [];
  Object.entries(text).forEach(([field, value]) => {
    if (value.trim() === '') {
      if (spanish) return;
      issues.push({
        field,
        message:
          'This field cannot be empty. Use Reset to go back to the default',
        blocksSave: true,
      });
      return;
    }
    const permitted = allowed[field] ?? [];
    placeholders(value)
      .filter((name) => !permitted.includes(name))
      .forEach((name) =>
        issues.push({
          field,
          message: `Unknown field %{${name}} (allowed: ${permitted.join(', ') || 'none'})`,
          blocksSave: true,
        }),
      );
    if (field !== 'sms') return;
    const rendered = interpolate(value, sampleValues);
    if (!rendered.startsWith(SMS_PREFIX)) {
      issues.push({
        field,
        message: `SMS must start with "${SMS_PREFIX}"`,
        blocksSave: false,
      });
    }
    if (spanish) {
      const segments = smsSegments(withoutStopLine(rendered, stopLine));
      if (segments > SPANISH_MAX_SEGMENTS) {
        issues.push({
          field,
          message: `SMS is ${segments} segments with long values, the limit is ${SPANISH_MAX_SEGMENTS}`,
          blocksSave: false,
        });
      }
      return;
    }
    if (!gsm7(rendered)) {
      issues.push({
        field,
        message:
          'SMS has a character outside GSM-7 (for example a curly quote or an emoji)',
        blocksSave: false,
      });
    }
    const length = smsLength(withoutStopLine(rendered, stopLine));
    if (length > SMS_LIMIT) {
      issues.push({
        field,
        message: `SMS is ${length} characters with long values, the limit is ${SMS_LIMIT}`,
        blocksSave: false,
      });
    }
  });
  return issues;
};
